'use client';

import useSWR, { mutate } from 'swr';
import { fleetApi } from '../api/axiosClient';
import FilterBuilder from '../utils/filterBuilder';
import { useMemo } from 'react';
import { normalizeApiResponse } from '../utils/response';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateVendorList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/vendor/search');
};

export const useVendorList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;
  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .addJoin('company', ['name'])
      .addJoin('business_type', ['name'])
      .setOrder(['name'], true);

    const tableName = searchKey === 'business_type' ? 'm_business_type' : null;
    const filterLike = searchKey === 'business_type' ? 'name' : searchKey;

    if (searchValue && searchValue.trim() !== '') {
      fb.like(filterLike, searchValue, 'AND', tableName);
    }
    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }

    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);
  const key = useMemo(() => ['/vendor/search', JSON.stringify(payload)], [payload]);
  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true
  });

  const result = normalizeApiResponse(data, error, isLoading);
  return result;
};

export const getVendorById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.get(`/vendor/${id}${getTzParam()}`);
    return response;
  } catch (error) {
    console.error('Failed to fetch vendor by ID:', error);
    throw error;
  }
};

export const createVendor = async (formData = {}) => {
  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.post(`/vendor/new${getTzParam()}`, body);

    if (response?.status === false || response?.error === true || response?.code >= 400) {
      throw { response: { data: response } };
    }
    revalidateVendorList();
    return response;
  } catch (error) {
    console.error('Error creating vendor:', error);
    throw error;
  }
};

export const updateVendor = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');

  try {
    const body = {
      ...formData
    };

    const response = await fleetApi.put(`/vendor/edit/${id}${getTzParam()}`, body);

    if (response?.status === false || response?.error === true || response?.code >= 400) {
      throw { response: { data: response } };
    }
    revalidateVendorList();
    return response;
  } catch (error) {
    console.error('Error updating vendor:', error);
    throw error;
  }
};

export const deleteVendor = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const response = await fleetApi.delete(`/vendor/delete/${id}${getTzParam()}`);

    if (response?.status === false || response?.error === true || response?.code >= 400) {
      throw { response: { data: response } };
    }
    revalidateVendorList();
    return response.data;
  } catch (error) {
    console.error('Error deleting vendor:', error);
    throw error;
  }
};
